import React, { useCallback, useEffect, useRef, useState } from "react";
import "lenis/dist/lenis.css";
import { AboutSection } from "./components/portfolio/AboutSection";
import { EdgeProgress } from "./components/portfolio/EdgeProgress";
import { ExperienceSection } from "./components/portfolio/ExperienceSection";
import { Footer } from "./components/portfolio/Footer";
import { Header } from "./components/portfolio/Header";
import { Hero } from "./components/portfolio/Hero";
import { Loader } from "./components/portfolio/Loader";
import { MetricsBand } from "./components/portfolio/MetricsBand";
import { ProjectsSection } from "./components/portfolio/ProjectsSection";
import { SignatureMark } from "./components/portfolio/SignatureMark";
import { StackSection } from "./components/portfolio/StackSection";
import { portfolio } from "./data/portfolio";
import { usePortfolioMotion } from "./hooks/usePortfolioMotion";
import "./portfolio.css";

function App() {
  const rootRef = useRef(null);
  const [introComplete, setIntroComplete] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const lenisRef = usePortfolioMotion(rootRef, introComplete);

  const finishIntro = useCallback(() => setIntroComplete(true), []);

  useEffect(() => {
    if (!("IntersectionObserver" in window)) return undefined;

    const sections = portfolio.navigation
      .map((item) => document.getElementById(item.id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-38% 0px -55%", threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const navigateTo = useCallback((id) => {
    const target = document.getElementById(id);
    if (!target) return;

    if (lenisRef.current) {
      lenisRef.current.scrollTo(target, { offset: -24, duration: 1.05 });
    } else {
      target.scrollIntoView({ behavior: "smooth" });
    }
  }, [lenisRef]);

  return (
    <div className="site-shell" ref={rootRef}>
      {!introComplete && <Loader name={portfolio.profile.name} onComplete={finishIntro} />}

      <Header
        portfolio={portfolio}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        activeSection={activeSection}
        onNavigate={navigateTo}
      />
      <EdgeProgress />
      <SignatureMark activeSection={activeSection} />

      <main id="main-content">
        <Hero
          profile={portfolio.profile}
          onViewWork={() => navigateTo("projects")}
        />
        <MetricsBand stats={portfolio.stats} />
        <AboutSection about={portfolio.about} email={portfolio.profile.email} />
        <StackSection groups={portfolio.stack} />
        <ExperienceSection experience={portfolio.experience} />
        <ProjectsSection projects={portfolio.projects} />
      </main>

      <Footer
        profile={portfolio.profile}
        socials={portfolio.socials}
        version={portfolio.version}
        onBackToTop={() => navigateTo("home")}
      />
    </div>
  );
}

export default App;
